// @ts-strict-ignore
import Form from 'antd/lib/form';
import * as React from 'react';
import {
    BucketMember,
    BuildDistroMemberPermissionLevel,
    BuildDistroMemberType,
    OrgUser,
    UserGroup,
    UserGroupId,
} from '../../types';
import Space from 'antd/lib/space';
import Select from 'antd/lib/select';
import { selectOrgGroups, selectUser } from '../../selectors/selectors';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    displayUserNameLong,
    displayTextForUserGroup,
    displayTextForBuildBucketPermission,
} from '../../helpers/displayHelpers';
import { DrawerList } from './mobileComponents/ResponsiveDrawer';
import { antGrey, getAvatarColorForName } from '../styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import { TeamOutlined } from '@ant-design/icons';
import Avatar from 'antd/lib/avatar';
import { EditMemberRoleSelect, RemoveMemberButton } from './CommonComponents';
import { selectAppOrgUsers } from '@/selectors/memoizedSelectors';

const REMOVE_MEMBER_VALUE = 'REMOVE_MEMBER_VALUE';

export const BucketRoleSelect: React.FC<{
    shouldShowRemove?: boolean;
    isDisabled?: boolean;
    isLoading?: boolean;
    defaultPermissionLevel?: BuildDistroMemberPermissionLevel;
    onSelect: (value: BuildDistroMemberPermissionLevel | typeof REMOVE_MEMBER_VALUE) => void;
}> = (props) => {
    const { onSelect, shouldShowRemove, isLoading, isDisabled, defaultPermissionLevel, ...rest } = props;
    return (
        <Select
            onSelect={onSelect}
            dropdownStyle={{
                width: 110,
            }}
            {...rest}
            defaultValue={defaultPermissionLevel}
            disabled={isDisabled}
            loading={isLoading}
            popupMatchSelectWidth={false}
            bordered={false}
            options={[
                ...Object.values(BuildDistroMemberPermissionLevel).map((permissionLevel) => ({
                    label: (
                        <Text
                            ellipsis
                            style={{
                                verticalAlign: 'middle',
                            }}
                        >
                            {displayTextForBuildBucketPermission(permissionLevel)}
                        </Text>
                    ),
                    value: permissionLevel,
                })),
                ...(shouldShowRemove ?
                    [
                        {
                            label: <Text type="danger">Remove</Text>,
                            value: REMOVE_MEMBER_VALUE,
                        },
                    ]
                :   []),
            ]}
        />
    );
};

const isUserGroupId = (user: OrgUser | UserGroupId): user is UserGroupId => {
    return typeof user === 'string';
};

const getUserOrGroupMember = (
    memberId: string,
    appUsers: OrgUser[],
    appGroups: UserGroupId[],
): OrgUser | UserGroupId | null => {
    const appUser = appUsers.find((user) => user.id === memberId);
    const appGroup = appGroups.find((role) => role === memberId);

    if (appUser) {
        return appUser;
    }

    if (appGroup) {
        return appGroup;
    }

    return null;
};

const nameForUserOrGroup = (user: OrgUser | UserGroupId | null, groups: Record<UserGroupId, UserGroup>): string => {
    if (!user) {
        return '';
    }

    if (isUserGroupId(user)) {
        return displayTextForUserGroup(groups[user]);
    }

    return displayUserNameLong(user);
};

export interface AddEditBucketMembersProps {
    initialMembers: BucketMember[];
    excludedMembers: BucketMember[];
    appId: string;
    orgId: string;
    onMembersChange: (members: BucketMember[]) => void;
}

export const AddEditBucketMembers: React.FC<AddEditBucketMembersProps> = ({
    initialMembers,
    excludedMembers,
    appId,
    orgId,
    onMembersChange,
}) => {
    const [form] = Form.useForm();

    const appUsers = useSelector((state: RootState) =>
        selectAppOrgUsers(state, appId).filter(
            (user) => !excludedMembers.some((excludedMember) => excludedMember.id === user.id),
        ),
    );

    const groups = useSelector((state: RootState) => {
        return selectOrgGroups(state, orgId);
    });

    const appGroups = Object.values(groups).filter(
        (group) => !group.disabled && !excludedMembers.some((excludedMember) => excludedMember.id === group.id),
    );

    const members = Form.useWatch('members', form);

    return (
        <Form
            form={form}
            layout="vertical"
            onValuesChange={(_, values) => {
                const members = values.members as BucketMember[];
                onMembersChange(members);
            }}
            preserve={false}
            initialValues={{
                members: (initialMembers ?? []).filter(
                    (member) => !excludedMembers.some((excludedMember) => excludedMember.id === member.id),
                ),
            }}
        >
            <Form.List name="members">
                {(fields, { add, remove }) => (
                    <Space
                        direction="vertical"
                        style={{
                            width: '100%',
                        }}
                    >
                        <div
                            style={{
                                padding: 16,
                                backgroundColor: antGrey.grey2,
                                borderRadius: 4,
                            }}
                        >
                            <Select
                                showSearch
                                placeholder="Add team member(s) or user group(s)"
                                style={{ width: '100%' }}
                                value={null}
                                defaultActiveFirstOption={false}
                                autoClearSearchValue
                                onSelect={(value) => {
                                    const memberId = value as string;

                                    const member = getUserOrGroupMember(memberId, appUsers, Object.keys(groups));

                                    if (member) {
                                        if (isUserGroupId(member)) {
                                            add({
                                                id: member,
                                                type: BuildDistroMemberType.Group,
                                                permissionLevel: BuildDistroMemberPermissionLevel.Tester,
                                            });
                                        } else {
                                            add({
                                                id: member.id,
                                                type: BuildDistroMemberType.User,
                                                permissionLevel: BuildDistroMemberPermissionLevel.Tester,
                                            });
                                        }
                                    }
                                }}
                                filterOption={(input, option: any) => {
                                    if (option?.options) {
                                        // return false for the 'root' options that contain either users or groups
                                        return false;
                                    }

                                    const maybeGroupId = option?.value;
                                    if (!maybeGroupId) {
                                        return false;
                                    }
                                    const maybeGroup = groups[maybeGroupId];
                                    const matchesGroup =
                                        maybeGroup?.name.toLowerCase().includes(input.toLowerCase()) ?? false;
                                    if (matchesGroup) {
                                        return true;
                                    }

                                    const maybeUserId = option?.value;
                                    return maybeUserId.toLowerCase().includes(input.toLowerCase());
                                }}
                                options={[
                                    {
                                        label: 'Users',
                                        options: appUsers
                                            .filter((user) => !members?.some((member) => member.id === user.id))
                                            .map((user) => ({
                                                label: displayUserNameLong(user),
                                                value: user.id,
                                            })),
                                    },
                                    {
                                        label: 'User groups',
                                        options: appGroups
                                            .filter((group) => !members?.some((member) => member.id === group))
                                            .map((group) => ({
                                                label: displayTextForUserGroup(group),
                                                value: group.id,
                                            })),
                                    },
                                ]}
                            />
                        </div>

                        <DrawerList
                            emptyText="No members yet"
                            items={fields.map((field) => {
                                const id = form.getFieldValue(['members', field.name, 'id']);

                                const userOrGroup = getUserOrGroupMember(id, appUsers, Object.keys(groups));

                                if (!userOrGroup) {
                                    return null;
                                }

                                const name = nameForUserOrGroup(userOrGroup, groups);

                                let icon: React.ReactNode;
                                if (isUserGroupId(userOrGroup)) {
                                    icon = (
                                        <TeamOutlined
                                            style={{
                                                fontSize: 18,
                                                color: antGrey.grey5,
                                            }}
                                        />
                                    );
                                } else {
                                    icon = (
                                        <Avatar
                                            style={{
                                                fontSize: 12,
                                                border: 'none',
                                                backgroundColor: getAvatarColorForName(name),
                                                flexShrink: 0,
                                            }}
                                            size={25}
                                            src={userOrGroup.commsAvatars?.image32}
                                        >
                                            {name.charAt(0).toUpperCase()}
                                        </Avatar>
                                    );
                                }

                                return {
                                    title: (
                                        <Space>
                                            {icon}
                                            <Text
                                                ellipsis
                                                style={{
                                                    color: antGrey.grey8,
                                                }}
                                            >
                                                {name}
                                            </Text>
                                        </Space>
                                    ),
                                    description: (
                                        <Space>
                                            <Form.Item name={[field.name, 'permissionLevel']} noStyle>
                                                <BucketRoleSelect
                                                    defaultPermissionLevel={BuildDistroMemberPermissionLevel.Tester}
                                                    onSelect={(value) => {
                                                        if (value === REMOVE_MEMBER_VALUE) {
                                                            remove(field.name);
                                                        }
                                                    }}
                                                    shouldShowRemove
                                                />
                                            </Form.Item>
                                        </Space>
                                    ),
                                };
                            })}
                        />
                    </Space>
                )}
            </Form.List>
        </Form>
    );
};

export interface MobileAddEditBucketMembersProps {
    members: BucketMember[];
    excludedMembers: BucketMember[];
    appId: string;
    orgId: string;
    bucketId: string;
    isEditing: boolean;
}

export const MobileAddEditBucketMembers: React.FC<MobileAddEditBucketMembersProps> = ({
    members,
    appId,
    orgId,
    bucketId,
    excludedMembers,
    isEditing,
}) => {
    const appUsers = useSelector((state: RootState) =>
        selectAppOrgUsers(state, appId).filter(
            (user) => !excludedMembers.some((excludedMember) => excludedMember.id === user.id),
        ),
    );

    const groups = useSelector((state: RootState) => {
        return selectOrgGroups(state, orgId);
    });

    const currentUser = useSelector((state: RootState) => selectUser(state));

    const currentBucketMember = React.useMemo(() => {
        return members.find((member) => {
            return member.type === BuildDistroMemberType.User && member.id === currentUser.id;
        });
    }, [members, currentUser]);

    const isCurrentMemberAdmin = !!(currentBucketMember?.permissionLevel === BuildDistroMemberPermissionLevel.Admin);

    return (
        <DrawerList
            emptyText="No members yet"
            items={(members ?? []).map((member) => {
                const userOrGroup = getUserOrGroupMember(member.id, appUsers, Object.keys(groups));

                if (!userOrGroup) {
                    return null;
                }

                const name = nameForUserOrGroup(userOrGroup, groups);

                let icon: React.ReactNode;
                if (isUserGroupId(userOrGroup)) {
                    icon = (
                        <TeamOutlined
                            style={{
                                fontSize: 18,
                                color: antGrey.grey5,
                            }}
                        />
                    );
                } else {
                    icon = (
                        <Avatar
                            style={{
                                fontSize: 12,
                                border: 'none',
                                backgroundColor: getAvatarColorForName(name),
                                flexShrink: 0,
                            }}
                            size={25}
                            src={userOrGroup.commsAvatars?.image32}
                        >
                            {name.charAt(0).toUpperCase()}
                        </Avatar>
                    );
                }

                const isCurrentUser = member.type === BuildDistroMemberType.User && member.id === currentUser.id;

                const shouldDisablePermissionsUpdate = isCurrentUser || !isCurrentMemberAdmin;

                return {
                    title: (
                        <Space
                            style={{
                                minWidth: 0,
                            }}
                        >
                            {icon}
                            <Text
                                ellipsis
                                style={{
                                    color: antGrey.grey8,
                                }}
                            >
                                {name}
                            </Text>
                        </Space>
                    ),
                    description: (
                        <Space>
                            {isEditing ?
                                <>
                                    <EditMemberRoleSelect
                                        bucketId={bucketId}
                                        appId={appId}
                                        orgId={orgId}
                                        memberId={member.id}
                                        isDisabled={shouldDisablePermissionsUpdate}
                                        defaultPermissionLevel={member.permissionLevel}
                                    />
                                    <RemoveMemberButton
                                        memberId={member.id}
                                        bucketId={bucketId}
                                        appId={appId}
                                        orgId={orgId}
                                        isDisabled={
                                            member.type === BuildDistroMemberType.User && member.id === currentUser.id
                                        }
                                    />
                                </>
                            :   <Text>{displayTextForBuildBucketPermission(member.permissionLevel)}</Text>}
                        </Space>
                    ),
                };
            })}
        />
    );
};
